// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - TGE EXPORT MANAGER
// Pulls real data from Supabase for TGE snapshot and CSV/JSON export
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './utils/supabaseClient';

// ═══════════════════════════════════════════════════════════════════════════
// FETCH ALL USER DATA FROM SUPABASE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fetch all user_stats rows from Supabase (admin use only)
 * Returns full dataset for TGE snapshot
 */
export async function getAllUserData() {
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .select('wallet_address, total_purifications, total_objects_cleaned, total_purity_earned, total_sui_reclaimed, total_toilet_purifications, last_purification, updated_at')
      .order('total_purity_earned', { ascending: false });

    if (error) {
      console.error('TGE export fetch error:', error);
      return [];
    }

    // Fetch usernames in batch
    const addresses = (data || []).map(r => r.wallet_address);
    let usernameMap = {};
    if (addresses.length > 0) {
      const { data: usernames } = await supabase
        .from('usernames')
        .select('wallet_address, username')
        .in('wallet_address', addresses);
      (usernames || []).forEach(r => { usernameMap[r.wallet_address] = r.username; });
    }

    return (data || []).map((row, index) => ({
      rank: index + 1,
      walletAddress: row.wallet_address,
      username: usernameMap[row.wallet_address] || null,
      totalPurifications: row.total_purifications || 0,
      totalObjectsCleaned: row.total_objects_cleaned || 0,
      totalPurityEarned: row.total_purity_earned || 0,
      totalSuiReclaimed: row.total_sui_reclaimed || 0,
      totalToiletPurifications: row.total_toilet_purifications || 0,
      // Points = 1:1 with purity for TGE airdrop
      distilAllocation: row.total_purity_earned || 0,
      lastPurification: row.last_purification,
    }));
  } catch (err) {
    console.error('getAllUserData failed:', err);
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// TGE STATS SUMMARY
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get aggregate TGE stats for admin dashboard
 */
export async function getTGEStats() {
  const users = await getAllUserData();

  if (users.length === 0) {
    return {
      totalUsers: 0,
      totalPoints: 0,
      totalDistil: 0,
      averageDistil: 0,
      topUser: null,
      toiletModeUsers: 0,
    };
  }

  const totalPoints = users.reduce((sum, u) => sum + u.totalPurityEarned, 0);
  const totalDistil = users.reduce((sum, u) => sum + u.distilAllocation, 0);
  const toiletUsers = users.filter(u => u.totalToiletPurifications > 0).length;

  return {
    totalUsers: users.length,
    totalPoints,
    totalDistil,
    averageDistil: users.length > 0 ? Math.floor(totalDistil / users.length) : 0,
    topUser: users[0] || null,
    toiletModeUsers: toiletUsers,
    snapshotTime: new Date().toISOString(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Download TGE snapshot as CSV
 */
export async function downloadCSV() {
  const users = await getAllUserData();

  if (users.length === 0) {
    alert('No user data to export yet.');
    return;
  }

  const headers = [
    'Rank', 'Wallet Address', 'Username',
    'Total Purifications', 'Total Objects Cleaned',
    'Total Purity (Points)', 'SUI Reclaimed',
    'Toilet Purifications', '$DISTIL Allocation',
    'Last Purification'
  ];

  const rows = users.map(u => [
    u.rank,
    u.walletAddress,
    u.username || '',
    u.totalPurifications,
    u.totalObjectsCleaned,
    u.totalPurityEarned,
    u.totalSuiReclaimed.toFixed(6),
    u.totalToiletPurifications,
    u.distilAllocation,
    u.lastPurification || '',
  ]);

  const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `distil_tge_snapshot_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Download TGE snapshot as JSON
 */
export async function downloadJSON() {
  const users = await getAllUserData();
  const stats = await getTGEStats();

  if (users.length === 0) {
    alert('No user data to export yet.');
    return;
  }

  const payload = {
    snapshotDate: new Date().toISOString(),
    summary: stats,
    allocations: users,
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `distil_tge_snapshot_${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export default { getAllUserData, getTGEStats, downloadCSV, downloadJSON };
